import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faMapMarkerAlt, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ParticleNetwork from '../components/ParticleNetwork';
import '../styles/ContactPage.css';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({
      submitted: true,
      success: true,
      message: t('contact.success_message')
    });

    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });

    setTimeout(() => {
      setFormStatus({
        submitted: false,
        success: false,
        message: ''
      });
    }, 5000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <ParticleNetwork />
        <div className="container">
          <motion.div
            className="hero-centered-content"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="breadcrumbs">
              <Link to="/">{t('nav.home')}</Link>
              <span className="separator">/</span>
              <span className="current">{t('nav.contact')}</span>
            </div>

            <motion.div className="hero-main" variants={itemVariants}>
              <h1 className="hero-title">{t('contact.title')}</h1>
              <div className="hero-accent-line"></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Content Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info Cards */}
            <motion.div
              className="contact-sidebar"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="contact-card info-card">
                <h2>{t('contact.get_in_touch')}</h2>
                <p>{t('contact.get_in_touch_desc')}</p>

                <div className="info-items">
                  <div className="info-item">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                    </div>
                    <div className="info-content">
                      <h3>{t('contact.address_title')}</h3>
                      <p>{t('footer.address_full')}</p>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <div className="info-content">
                      <h3>{t('contact.phone_title')}</h3>
                      <p>+90 262 777 00 77</p>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="icon-wrapper">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                    <div className="info-content">
                      <h3>{t('contact.email_title')}</h3>
                      <p>info@kusgrup.com.tr.tr</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="contact-form-wrapper"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="contact-card form-card">
                <h2>{t('contact.send_message')}</h2>

                {formStatus.submitted && (
                  <motion.div
                    className={`form-alert ${formStatus.success ? 'success' : 'error'}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    {formStatus.message}
                  </motion.div>
                )}

                <form className="modern-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">{t('contact.form_name')}</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.form_name_ph')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">{t('contact.form_email')}</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.form_email_ph')}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">{t('contact.form_subject')}</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form_subject_ph')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">{t('contact.form_message')}</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form_message_ph')}
                    ></textarea>
                  </div>

                  <motion.button
                    type="submit"
                    className="submit-btn"
                    whileHover={{ translateY: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{t('contact.form_submit')}</span>
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <div className="map-header">
            <h2 className="section-title">{t('contact.location')}</h2>
            <div className="title-underline"></div>
          </div>
          <motion.div
            className="map-wrapper"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d24149.56019669338!2d29.445003!3d40.834662!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cb2736e5b28493%3A0xc44b86d9ae4ebf12!2sMarmara%20Teknik%20Konvey%C3%B6r%20Makina%20Palet!5e0!3m2!1str!2str!4v1751890076654!5m2!1str!2str"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="KUS GRUP Konum"
            ></iframe>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;